// src/pages/Profile/MyProfile.jsx
// -----------------------------------------------------------------------------
// "My Profile" screen (Requirement #24: create and maintain User Profiles).
// Any logged-in user can view their details and change their name / phone via
// GET + PUT /api/Auth/me. Email, role and organization are read-only here -
// those are managed by an Admin on the Users screen.
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../api/authService';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';

export default function MyProfile() {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ first_Name: '', last_Name: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    authService
      .getMyProfile()
      .then((res) => {
        const p = res.data || {};
        setProfile(p);
        setForm({ first_Name: p.first_Name || '', last_Name: p.last_Name || '', phone: p.phone || '' });
      })
      .catch(() => toast.error('Failed to load your profile.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.first_Name.trim() || !form.last_Name.trim()) {
      setError('First name and last name are required.');
      return;
    }

    setSaving(true);
    try {
      const res = await authService.updateMyProfile(form);
      const p = res.data || {};
      setProfile(p);
      // Keep the header name in sync without a re-login.
      updateUser({ firstName: p.first_Name, lastName: p.last_Name, phone: p.phone || '' });
      toast.success('Profile updated.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader label="Loading profile..." />;
  if (!profile) return <p>Profile not available.</p>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>My Profile</h1>
          <div className="subtitle">View and update your personal details.</div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 640 }}>
        {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input id="firstName" value={form.first_Name} onChange={handleChange('first_Name')} maxLength={50} required />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input id="lastName" value={form.last_Name} onChange={handleChange('last_Name')} maxLength={50} required />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input id="phone" value={form.phone} onChange={handleChange('phone')} maxLength={20} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input value={profile.email || ''} readOnly disabled />
            </div>
            <div className="form-group">
              <label>Role</label>
              <input value={profile.role || ''} readOnly disabled />
            </div>
          </div>

          <p className="text-muted" style={{ fontSize: 13 }}>
            Status: <StatusBadge value={profile.status} />
            {profile.last_Login_Dttm && <> &middot; Last login {new Date(profile.last_Login_Dttm).toLocaleString()}</>}
          </p>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
            <Link to={`/change-password?email=${encodeURIComponent(profile.email || '')}`}>Change password</Link>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
